<template>
  <modal
    :show="isVisible"
    :title="t('Selecionar Cliente')"
    elem-id="modal_so-cliente-select"
    size="large"
    :close-label="t('Cancelar')"
    :show-save="false"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <data-table
        ref="datatableRef"
        :config="datatableConfig"
        :endpoint="endpoint"
        @data-loaded="handleDataLoaded"
        @data-error="handleDataError"
        @button-click="handleButtonClick"
        @edit-click="handleEditClick"
      />
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from '../grid/DataTable.vue';
import { eventBus } from '../eventBus';
import { log } from '../utils/DebugLogger';

// No props needed — endpoint uses the global franchise filter

const emit = defineEmits<{
  (e: 'select', data: any): void;
  (e: 'close'): void;
}>();

const { t } = useI18n();

// Reactive data
const isVisible = ref(false);
const datatableRef = ref<any>(null);

// Uses carrega_clientes_todos — same as ClienteTable but WITHOUT date filter
// (clients should always be selectable regardless of the selected date range)
const endpoint = computed(() => '/index.php/clientes/carrega_clientes_todos');

const tableColumns = computed(() => [
  {
    data: 'cliente_id',
    title: 'ID',
    width: 70,
    sortable: true,
    renderType: 'clickable_id',
    dataType: 'text',
    mergedColumns: [
      { data: 'franquia', title: t('Franquia') },
    ],
  },
  {
    data: null,
    title: '',
    width: 120,
    sortable: false,
    renderType: 'clickable_button',
    buttonText: t('Selecionar'),
    buttonIcon: 'pi pi-check',
    buttonClass: 'p-button-success p-button-sm',
    buttonTestId: (row: any) => `so-cliente-select-${row.cliente_id}-btn`,
    buttonAction: 'select'
  },
  {
    data: 'nome',
    title: t('Nome'),
    width: 200,
    sortable: true,
    dataType: 'text'
  },
  {
    data: 'telefone_completo',
    title: t('Telefone'),
    width: 170,
    sortable: true,
    dataType: 'text'
  },
  {
    data: 'documento',
    title: t('Documento'),
    width: 150,
    sortable: true,
    dataType: 'text'
  }
]);

const datatableConfig = computed(() => ({
  filename: 'clientes_selecao',
  columns: tableColumns.value,
  pageLength: 25,
  height: '450px',
  heightMode: 'fixed',
  pagination: true,
  rowSelection: 'single',
  sortable: true,
  showFilters: true,
  filterDisplay: 'row',
  stripedRows: true,
  showGridlines: true,
  size: 'small'
}));

// Methods
const open = (): void => {
  log('ClienteSelectModal', 'open', 'Opening modal');
  isVisible.value = true;
  nextTick(() => {
    datatableRef.value?.loadData?.();
  });
};

const handleClose = (): void => {
  isVisible.value = false;
  emit('close');
};

const handleButtonClick = (event: any): void => {
  if (event.action === 'select') {
    log('ClienteSelectModal', 'handleButtonClick', 'Client selected', event.data);
    emit('select', event.data);
    handleClose();
  }
};

const handleDataLoaded = (data: any[]): void => {
  log('ClienteSelectModal', 'handleDataLoaded', 'Loaded', data.length, 'clients');
};

const handleDataError = (error: any): void => {
  log('ClienteSelectModal', 'handleDataError', 'Error', error);
};

const handleEditClick = (clienteId: number | string): void => {
  log('ClienteSelectModal', 'handleEditClick', 'Opening client modal for ID', clienteId);
  eventBus.emit('modal.cliente.open', { id: clienteId });
};

defineExpose({ open });
</script>
